import { computed } from '@vue/composition-api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import {
  IWorkflowStateThreshold,
  IWorkflowStateThresholdData,
  WorkflowStateThreshold
} from '@/models/workflow';
import providerInstance from '@/services/provider';

// Split into CRUD composables

// Fetch all thresholds
export function useFetchThresholds(workflowId: number, stateId: number) {
  const getThresholdsQueryKey = () => [
    'workflow',
    workflowId,
    'states',
    stateId,
    'thresholds'
  ];

  const {
    data: thresholds,
    isLoading: isLoadingThresholds,
    error: thresholdsError,
    refetch: refetchThresholds
  } = useQuery({
    queryKey: getThresholdsQueryKey(),
    queryFn: () =>
      providerInstance.workflow.fetchWorkflowStateThresholds(
        workflowId,
        stateId
      ),
    select: data => data.map(threshold => new WorkflowStateThreshold(threshold))
  });

  return {
    thresholds,
    isLoadingThresholds,
    thresholdsError,
    refetchThresholds
  };
}

// Fetch single threshold
export function useFetchThreshold(
  workflowId: number,
  stateId: number,
  thresholdId: number
) {
  const getThresholdQueryKey = () => [
    'workflow',
    workflowId,
    'states',
    stateId,
    'thresholds',
    thresholdId
  ];

  const {
    data: threshold,
    isLoading: isLoadingThreshold,
    error: thresholdError
  } = useQuery({
    queryKey: getThresholdQueryKey(),
    queryFn: () =>
      providerInstance.workflow.fetchWorkflowStateThreshold(
        workflowId,
        stateId,
        thresholdId
      ),
    enabled: !!thresholdId,
    select: threshold => new WorkflowStateThreshold(threshold)
  });

  return {
    threshold: computed(() => threshold.value),
    isLoadingThreshold,
    thresholdError
  };
}

// Create threshold
export function useCreateThreshold(workflowId: number, stateId: number) {
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isLoading, error, reset } = useMutation({
    mutationFn: (data: IWorkflowStateThresholdData) =>
      providerInstance.workflow.createWorkflowStateThreshold(
        workflowId,
        stateId,
        data
      ),
    onSuccess: () => {
      queryClient.invalidateQueries([
        'workflow',
        workflowId,
        'states',
        stateId,
        'thresholds'
      ]);
    }
  });

  return {
    createThreshold: mutate,
    createThresholdAsync: mutateAsync,
    isCreatingThreshold: computed(() => isLoading.value),
    createThresholdError: computed(() => error.value),
    createThresholdErrorReset: reset
  };
}

// Update threshold
export function useUpdateThreshold(workflowId: number, stateId: number) {
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isLoading, error, reset } = useMutation({
    mutationFn: (data: IWorkflowStateThresholdData) =>
      providerInstance.workflow.updateWorkflowStateThreshold(
        workflowId,
        stateId,
        data
      ),
    onSuccess: (_, threshold) => {
      queryClient.invalidateQueries([
        'workflow',
        workflowId,
        'states',
        stateId,
        'thresholds'
      ]);
      if (threshold.id) {
        queryClient.invalidateQueries([
          'workflow',
          workflowId,
          'states',
          stateId,
          'thresholds',
          threshold.id
        ]);
      }
    }
  });

  return {
    updateThreshold: mutate,
    updateThresholdAsync: mutateAsync,
    isUpdatingThreshold: computed(() => isLoading.value),
    updateThresholdError: computed(() => error.value),
    updateThresholdErrorReset: reset
  };
}

// Delete threshold mutation
export function useDeleteThreshold(workflowId: number, stateId: number) {
  const queryClient = useQueryClient();

  const { reset, mutate, mutateAsync, isLoading, error } = useMutation({
    mutationFn: (thresholdId: number) =>
      providerInstance.workflow.deleteWorkflowStateThreshold(
        workflowId,
        stateId,
        thresholdId
      ),
    onSuccess: (_, thresholdId) => {
      queryClient.invalidateQueries([
        'workflow',
        workflowId,
        'states',
        stateId,
        'thresholds'
      ]);
      queryClient.invalidateQueries([
        'workflow',
        workflowId,
        'states',
        stateId,
        'thresholds',
        thresholdId
      ]);
    }
  });

  return {
    deleteThresholdErrorReset: reset,
    deleteThreshold: mutate,
    deleteThresholdAsync: mutateAsync,
    isDeletingThreshold: computed(() => isLoading.value),
    deleteThresholdError: computed(() => error.value)
  };
}
